import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, catLabel, TALENT_TO_TRAINING } from "@/lib/constants";
import api from "@/lib/api";
import { Search, MapPin, Bookmark, TrendingUp, ShieldCheck, Flame, Sparkles, Drama, Shield, Stethoscope, Skull, Scale, Briefcase, Heart, ChevronRight, Music2, Mic2, Users, Folder, BadgeCheck, X } from "lucide-react";
import TrainingAdsStrip from "@/components/TrainingAdsStrip";
import SponsoredBanner from "@/components/SponsoredBanner";
import FeaturedListings from "@/components/FeaturedListings";

// Character roles → maps to actor category filter + name-based search hint.
// Icons drawn from lucide-react; kept intentionally symbolic (no emoji).
const CHARACTERS = [
  { key: "police",      label: "Police",         icon: Shield,       color: "#3B82F6", tag: "police" },
  { key: "doctor",      label: "Doctor",         icon: Stethoscope,  color: "#10B981", tag: "doctor" },
  { key: "villain",     label: "Villain",        icon: Skull,        color: "#DC2626", tag: "villain" },
  { key: "lawyer",      label: "Lawyer",         icon: Scale,        color: "#F5C462", tag: "lawyer" },
  { key: "ceo",         label: "CEO",            icon: Briefcase,    color: "#A78BFA", tag: "corporate" },
  { key: "romantic",    label: "Romantic Lead",  icon: Heart,        color: "#F472B6", tag: "romantic" },
];

// Trending Talents pill mapping — key must exist in CATEGORIES so filter works
const TRENDING_TALENTS = [
  { key: "actor",         label: "Acting",     icon: Drama },
  { key: "singer",        label: "Singing",    icon: Mic2 },
  { key: "dancer",        label: "Dance",      icon: Music2 },
  { key: "model",         label: "Modeling",   icon: Sparkles },
  { key: "comedian",      label: "Comedy",     icon: Users },
  { key: "voice-artist",  label: "Voice",      icon: Mic2 },
];

export default function Discover() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [city, setCity] = useState("");
  const [users, setUsers] = useState([]);
  const [trending, setTrending] = useState([]);
  const [privateMode, setPrivateMode] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [rising, setRising] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [trendingCats, setTrendingCats] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const searchWrapRef = useRef(null);

  const search = async () => {
    const params = {};
    if (q) params.q = q;
    if (cat && cat !== "all") params.category = cat;
    if (city) params.city = city;
    const { data } = await api.get("/discover", { params });
    setUsers(data.users || []);
    setSimilar(data.similar_creators || []);
    setPrivateMode(data.private_mode);
  };

  // Autocomplete — fetch as user types (250ms debounce)
  useEffect(() => {
    if (!q || q.trim().length < 1) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const { data } = await api.get(`/search/autocomplete?q=${encodeURIComponent(q.trim())}`);
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
      finally { setSuggestLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close suggestions on outside click
  useEffect(() => {
    const onClick = (e) => { if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pickSuggestion = (s) => {
    setShowSuggestions(false);
    if (s.type === "creator") { nav(`/${s.value}`); }
    else if (s.type === "category") { setCat(s.value); setQ(""); }
    else if (s.type === "city") { setCity(s.value); setQ(""); }
  };

  useEffect(() => {
    search();
    api.get("/videos/trending").then(r => setTrending(r.data.videos || [])).catch(() => {});
    api.get("/videos/bookmarks/mine").then(r => setBookmarks(r.data.videos || [])).catch(() => {});
    api.get("/discover/rising").then(r => setRising(r.data.creators || [])).catch(() => {});
    api.get("/discover/fresh").then(r => setFresh(r.data.creators || [])).catch(() => {});
    api.get("/discover/trending-categories").then(r => setTrendingCats(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(search, 350);
    return () => clearTimeout(t);
  }, [q, cat, city]);

  return (
    <div data-testid="discover-page" className="min-h-screen pb-24 bg-[#0A0605]">
      <TopBar subtitle="Discover"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-5">
        <BackButton fallback="/app" label="Back"/>

        {/* Search + Filter with real-time autocomplete */}
        <div ref={searchWrapRef} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none z-10" size={16}/>
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Search talent, skills, names..."
            className="pl-9 pr-9 bg-zinc-900 border-zinc-800 h-11 focus-visible:ring-[#DC2626]"
          />
          {q && (
            <button data-testid="search-clear" onClick={() => { setQ(""); setSuggestions([]); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white p-1">
              <X size={14}/>
            </button>
          )}
          {showSuggestions && q && (suggestions.length > 0 || suggestLoading) && (
            <div data-testid="autocomplete-dropdown" className="absolute z-30 mt-1.5 w-full rounded-2xl border border-zinc-800 bg-[#0f0908]/95 backdrop-blur-md shadow-xl shadow-black/40 overflow-hidden">
              {suggestLoading && suggestions.length === 0 && <p className="p-3 text-xs text-zinc-500">Searching...</p>}
              {suggestions.map((s, i) => (
                <button
                  key={`${s.type}-${s.value}-${i}`}
                  data-testid={`autocomplete-${s.type}-${s.value}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSuggestion(s)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#DC2626]/10 border-b border-zinc-900/80 last:border-0 text-left transition-colors"
                >
                  {s.type === "creator" ? (
                    s.avatar
                      ? <img src={s.avatar} alt="" className="w-8 h-8 rounded-full object-cover border border-zinc-800"/>
                      : <div className="w-8 h-8 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/40 text-[#DC2626] text-xs font-semibold flex items-center justify-center">{(s.label || "?").slice(0,1).toUpperCase()}</div>
                  ) : s.type === "category" ? (
                    <div className="w-8 h-8 rounded-full bg-[#F5C462]/20 border border-[#F5C462]/40 text-[#F5C462] flex items-center justify-center"><Folder size={14}/></div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center"><MapPin size={14}/></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate flex items-center gap-1">
                      {s.label}
                      {s.type === "creator" && s.verified && <BadgeCheck size={12} className="text-[#F5C462] shrink-0"/>}
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate">{s.sub}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-600">{s.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="cat-filter" className="bg-zinc-900 border-zinc-800 h-10"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={14}/>
            <Input data-testid="city-filter" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="pl-9 bg-zinc-900 border-zinc-800 h-10 focus-visible:ring-[#DC2626]"/>
          </div>
        </div>

        <TrainingAdsStrip category={cat && cat !== "all" ? (TALENT_TO_TRAINING[cat] || null) : null} city={city || null} limit={6}/>
        {q && <SponsoredBanner placement="between_search"/>}
        {q && <SponsoredBanner placement="search"/>}
        {cat && cat !== "all" && <SponsoredBanner placement="category" category={cat}/>}
        <SponsoredBanner placement="between_categories"/>
        <FeaturedListings category={cat && cat !== "all" ? cat : null} city={city || null} limit={4}/>

        {/* ─── Rising Creators ─────────────────────────────────── */}
        {rising.length > 0 && (
          <HScrollSection title="Rising Creators" subtitle="Fastest growing this week" icon={Flame} iconColor="#DC2626" testid="rising-creators">
            {rising.map((u, i) => (
              <Link key={u.user_id} to={`/${u.username}`} data-testid={`rising-${u.username}`} className="shrink-0 w-40 rounded-2xl border border-zinc-800 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-3 hover:border-[#DC2626]/50 hover:shadow-lg hover:shadow-[#DC2626]/10 transition-all group">
                <div className="relative">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626]/30 to-[#DC2626]/5 border border-[#DC2626]/40 flex items-center justify-center text-[#DC2626] text-xl font-bold overflow-hidden">
                    {u.profile?.avatar ? <img src={u.profile.avatar} alt="" className="w-full h-full object-cover"/> : (u.name || "?").slice(0,1).toUpperCase()}
                  </div>
                  <span className="absolute -top-1 -right-1 bg-[#DC2626] text-white text-[9px] font-bold rounded-full px-1.5 h-4 flex items-center">#{i + 1}</span>
                </div>
                <p className="mt-2.5 font-semibold text-[13px] truncate text-white group-hover:text-[#F5C462] transition-colors">{u.name}</p>
                <p className="text-[11px] text-zinc-500 truncate">@{u.username}</p>
                <p className="text-[10px] mt-1 text-[#DC2626] truncate">{catLabel(u.category)}</p>
                {u.growth_pct !== undefined && u.growth_pct !== 0 ? (
                  <p className={`text-[10px] mt-1.5 font-semibold flex items-center gap-1 ${u.growth_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    <Flame size={9}/> {u.growth_pct >= 0 ? "+" : ""}{u.growth_pct}% growth
                  </p>
                ) : u.gained_this_week > 0 ? (
                  <p className="text-[10px] mt-1.5 text-emerald-400 font-semibold flex items-center gap-1">
                    <Flame size={9}/> +{u.gained_this_week} follower{u.gained_this_week === 1 ? "" : "s"}
                  </p>
                ) : null}
              </Link>
            ))}
          </HScrollSection>
        )}

        {/* ─── Fresh Faces ─────────────────────────────────────── */}
        {fresh.length > 0 && (
          <HScrollSection title="Fresh Faces" subtitle="Joined in the last 30 days" icon={Sparkles} iconColor="#F5C462" testid="fresh-faces">
            {fresh.map((u) => (
              <Link key={u.user_id} to={`/${u.username}`} data-testid={`fresh-${u.username}`} className="shrink-0 w-40 rounded-2xl border border-[#F5C462]/30 bg-gradient-to-br from-[#1a120a] to-[#0A0605] p-3 hover:border-[#F5C462]/70 hover:shadow-lg hover:shadow-[#F5C462]/10 transition-all group">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F5C462]/25 to-[#F5C462]/5 border border-[#F5C462]/40 flex items-center justify-center text-[#F5C462] text-xl font-bold overflow-hidden">
                  {u.profile?.avatar ? <img src={u.profile.avatar} alt="" className="w-full h-full object-cover"/> : (u.name || "?").slice(0,1).toUpperCase()}
                </div>
                <div className="flex items-center gap-1 mt-2.5">
                  <p className="font-semibold text-[13px] truncate text-white group-hover:text-[#F5C462] transition-colors">{u.name}</p>
                  <Sparkles size={10} className="text-[#F5C462] shrink-0"/>
                </div>
                <p className="text-[11px] text-zinc-500 truncate">@{u.username}</p>
                <p className="text-[10px] mt-1 text-[#F5C462] truncate">{catLabel(u.category)}</p>
                <p className="text-[10px] mt-1.5 text-zinc-400 italic">New arrival</p>
              </Link>
            ))}
          </HScrollSection>
        )}

        {/* ─── Photo Type Filters (iter 37) ────────────────────── */}
        <div data-testid="photo-type-filters" className="pt-2">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-[#F5C462]"/>
            <h3 className="font-display text-sm font-semibold text-white">Filter by look</h3>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
            {[
              {k: "headshot", l: "Headshots"},
              {k: "full_body", l: "Full Body"},
              {k: "half_body", l: "Half Body"},
              {k: "traditional", l: "Traditional"},
              {k: "western", l: "Western"},
              {k: "ethnic", l: "Ethnic"},
              {k: "formal", l: "Formal"},
              {k: "casual", l: "Casual"},
              {k: "commercial_look", l: "Commercial"},
              {k: "character_look", l: "Character"},
            ].map(pt => (
              <Link
                key={pt.k}
                to={`/app/discover?photo_type=${pt.k}`}
                data-testid={`photo-type-${pt.k}`}
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const { data } = await api.get(`/discover/by-photo-type?photo_type=${pt.k}`);
                    setUsers(data.users || []);
                    setSimilar([]);
                    setQ(""); setCat(""); setCity("");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } catch {}
                }}
                className="shrink-0 h-8 px-3.5 rounded-full text-xs font-medium border border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-[#F5C462]/50 hover:text-[#F5C462] transition-colors inline-flex items-center"
              >
                {pt.l}
              </Link>
            ))}
          </div>
          <p className="text-[10px] text-zinc-600 mt-1">Casting directors filter by look — tap a chip to see matching creators.</p>
        </div>

        {/* ─── Discover by Character ───────────────────────────── */}
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <Drama size={16} className="text-[#DC2626]"/>
            <h2 className="font-display text-lg font-semibold text-white">Discover by Character</h2>
          </div>
          <p className="text-[11px] text-zinc-500 -mt-2 mb-3">Casting a specific role? Tap the character.</p>
          <div data-testid="characters-grid" className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
            {CHARACTERS.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.key}
                  data-testid={`character-${c.key}`}
                  onClick={() => { nav(`/app/discover-character/${c.key}`); }}
                  className="group flex flex-col items-center gap-2 rounded-2xl border border-zinc-800 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-3 hover:border-[#F5C462]/40 hover:-translate-y-0.5 transition-all"
                  style={{"--char-color": c.color}}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center border-2 group-hover:scale-110 transition-transform"
                    style={{backgroundColor: `${c.color}20`, borderColor: `${c.color}80`, color: c.color}}
                  >
                    <Icon size={22} strokeWidth={2.25}/>
                  </div>
                  <p className="text-[11px] text-zinc-200 group-hover:text-white text-center font-medium leading-tight">{c.label}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Trending Talents ────────────────────────────────── */}
        <HScrollSection title="Trending Talents" subtitle="Top talent categories right now" icon={TrendingUp} iconColor="#F5C462" testid="trending-talents">
          {TRENDING_TALENTS.map((t) => {
            const Icon = t.icon;
            const stats = trendingCats.find(x => x.category === t.key);
            const videoCount = stats?.videos || 0;
            return (
              <button
                key={t.key}
                data-testid={`talent-cat-${t.key}`}
                onClick={() => { setCat(t.key); setQ(""); }}
                className="shrink-0 w-36 rounded-2xl border border-zinc-800 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-3.5 hover:border-[#F5C462]/50 hover:-translate-y-0.5 transition-all group text-left"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F5C462]/25 to-[#DC2626]/15 border border-[#F5C462]/30 flex items-center justify-center text-[#F5C462] group-hover:scale-105 transition-transform">
                  <Icon size={20} strokeWidth={2}/>
                </div>
                <p className="mt-2.5 font-semibold text-sm text-white group-hover:text-[#F5C462] transition-colors">{t.label}</p>
                <p className="text-[10px] text-zinc-500 mt-1">{videoCount > 0 ? `${videoCount} new video${videoCount === 1 ? "" : "s"}` : "Trending"}</p>
                <div className="mt-2 flex items-center gap-1 text-[10px] text-[#F5C462]/70 group-hover:text-[#F5C462]">
                  Explore <ChevronRight size={10}/>
                </div>
              </button>
            );
          })}
        </HScrollSection>

        {/* ─── Trending videos (existing) ─────────────────────── */}
        {trending.length > 0 && (
          <Section title="Trending Videos" icon={TrendingUp}>
            <div className="grid grid-cols-2 gap-3">
              {trending.slice(0, 4).map((v) => (
                <Link key={v.id} to={`/v/${v.id}`} data-testid={`trending-${v.id}`} className="rounded-xl border border-zinc-800 bg-[#180D0D] p-3 hover:border-[#DC2626]/40 transition-colors">
                  <p className="font-medium text-sm line-clamp-1">{v.title || "Untitled"}</p>
                  <p className="text-xs text-zinc-400 mt-1">@{v.username} · {v.views || 0} views</p>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* ─── Talents grid (search results) ──────────────────── */}
        <Section title="Talents" icon={ShieldCheck}>
          {users.length === 0 ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-6 text-center">
                <p className="font-display text-lg font-semibold text-white">Naye chehre aa rahe hain</p>
                <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                  {q || cat || city ? "No talents match all your filters." : "Be the first face people find."}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {(q || cat || city) && (
                    <button data-testid="clear-filters" onClick={() => { setQ(""); setCat(""); setCity(""); }} className="rounded-full border border-zinc-700 bg-zinc-900 text-white text-sm px-5 h-10 hover:border-[#DC2626]/40 transition-colors">
                      Clear filters
                    </button>
                  )}
                  <Link to="/app/upload" className="inline-flex items-center rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold px-5 h-10 text-sm transition-colors">Upload your performance</Link>
                </div>
              </div>
              {/* Similar Creators fallback — never a blank screen */}
              {similar.length > 0 && (
                <div data-testid="similar-creators" className="pt-1">
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={16} className="text-[#F5C462]"/>
                    <h3 className="font-display text-base font-semibold text-white">You might also like</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {similar.map((u) => (
                      <Link key={u.user_id} to={`/${u.username}`} data-testid={`similar-${u.username}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 hover:border-[#F5C462]/40 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-[#F5C462]/15 border border-[#F5C462]/30 flex items-center justify-center text-[#F5C462] text-lg font-semibold overflow-hidden">
                          {u.profile?.avatar ? <img src={u.profile.avatar} alt="" className="w-full h-full object-cover"/> : (u.name || "?").slice(0,1).toUpperCase()}
                        </div>
                        <p className="mt-3 font-semibold text-sm truncate">{u.name}</p>
                        <p className="text-xs text-zinc-400 truncate">@{u.username}</p>
                        <p className="text-xs mt-1 text-[#F5C462]">{catLabel(u.category)}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {users.map((u) => (
                <Link key={u.user_id} to={`/${u.username}`} data-testid={`talent-card-${u.username}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 hover:border-[#DC2626]/40 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/30 flex items-center justify-center text-[#DC2626] text-lg font-semibold overflow-hidden">
                    {u.profile?.avatar ? <img src={u.profile.avatar} alt="" className="w-full h-full object-cover"/> : (u.name || "?").slice(0,1).toUpperCase()}
                  </div>
                  <p className="mt-3 font-semibold text-sm truncate">{u.name}</p>
                  <p className="text-xs text-zinc-400 truncate">@{u.username}</p>
                  <p className="text-xs mt-1 text-[#DC2626]">{catLabel(u.category)}</p>
                  {u.profile?.city && <p className="text-xs text-zinc-400 mt-1 truncate">{u.profile.city}</p>}
                </Link>
              ))}
            </div>
          )}
        </Section>

        {bookmarks.length > 0 && (
          <Section title="Your Bookmarks" icon={Bookmark}>
            <div className="grid grid-cols-2 gap-3">
              {bookmarks.map((v) => (
                <Link key={v.id} to={`/v/${v.id}`} data-testid={`bookmark-${v.id}`} className="rounded-xl border border-zinc-800 bg-[#180D0D] p-3 hover:border-[#DC2626]/40 transition-colors">
                  <p className="font-medium text-sm line-clamp-1">{v.title || "Untitled"}</p>
                  <p className="text-xs text-zinc-400 mt-1">@{v.username}</p>
                </Link>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="pt-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#DC2626]"/>
        <h2 className="font-display text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function HScrollSection({ title, subtitle, icon: Icon, iconColor, testid, children }) {
  return (
    <div className="pt-2" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} style={{color: iconColor}}/>
        <h2 className="font-display text-lg font-semibold text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-[11px] text-zinc-500 mb-3">{subtitle}</p>}
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
        {children}
      </div>
    </div>
  );
}
